import * as React from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import {
  Activity,
  ArrowRight,
  BatteryCharging,
  BatteryLow,
  BatteryMedium,
  ChevronDown,
  ChevronLeft,
  Cpu,
  Download,
  Globe,
  HardDrive,
  Camera,
  EyeOff,
  Leaf,
  Link2,
  MonitorPlay,
  MonitorSmartphone,
  Power,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Save,
  Search,
  Settings2,
  ShieldCheck,
  Square,
  Tablet,
  Terminal,
  Thermometer,
  Trash2,
  Wifi,
  WifiOff,
} from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { useConfirm } from "@/components/confirm-dialog"
import {
  api,
  installBuild,
  listBuilds,
  sendCommand,
  type AppBuild,
  type Device,
  type DeviceEvent,
  type DeviceSettings,
  type DeviceStatus,
  type MirrorStatus,
  type ScreenFrame,
  type Screenshot,
  type SetSettingsResult,
  type SettingField,
  type SettingValue,
  type UpdateConfig,
} from "@/lib/api"
import { formatAbsolute, formatBytes, formatRelative, formatUptime } from "@/lib/format"
import { useDevicesStream } from "@/lib/useDevicesStream"
import { useLiveDomain } from "@/lib/useLiveDomain"

type LogLine = { timestamp?: string; level?: string; message?: string; raw?: string; backfill?: boolean }

/** גוף אירועי update_* שהמכשיר דוחף בזמן עדכון (REMOTE_CONTROL_API §4.4). */
type UpdateEventData = { packageName?: string; version?: string; error?: string }
type MonitorEvent = { event: string; data: UpdateEventData; at: number }

/** קודי שגיאה של המכשיר, בשפה של מי שקורא את המסך ולא של מי שכתב את הפרוטוקול. */
const COMMAND_ERRORS: Record<string, string> = {
  no_active_activity:
    `מסך האפליקציה אינו רשום לשליטה מרחוק. הפעל מחדש את האפליקציה ("הגדרות האפליקציה" ← "הפעל מחדש").`,
  device_offline: "המכשיר לא מחובר.",
  timeout: "המכשיר לא ענה בזמן.",
  disconnected: "החיבור למכשיר נותק באמצע הפקודה.",
  package_not_allowed: "החבילה אינה ברשימת ההיתר של המכשיר.",
  projection_consent_required:
    "שיקוף מסך מלא דורש אישור חד-פעמי על הטאבלט. לחץ \"בקש אישור מהטאבלט\".",
  consent_denied: "האישור נדחה על הטאבלט.",
  consent_dialog_failed: "לא ניתן להציג את דיאלוג האישור על הטאבלט.",
  unsupported_android_version: "גרסת האנדרואיד של המכשיר לא תומכת בשיקוף מסך.",
  window_unavailable: "חלון האפליקציה עדיין לא מוכן לצילום.",
  capture_failed: "הלכידה נכשלה במכשיר.",
  encode_failed: "קידוד התמונה נכשל במכשיר.",
  out_of_memory: "אין מספיק זיכרון במכשיר לצילום. נסה רזולוציה נמוכה יותר.",
  mirror_service_start_failed:
    "המכשיר לא הצליח להפעיל את שירות השיקוף. ייתכן שהמסך כבוי - הער את הטאבלט ונסה שוב.",
}

function describeCommandError(err: unknown) {
  const msg = (err as Error).message
  return COMMAND_ERRORS[msg] ?? msg
}

const TABS = [
  { value: "overview", label: "סקירה", icon: Tablet },
  { value: "display", label: "תצוגה", icon: Globe },
  { value: "screen", label: "מסך", icon: MonitorSmartphone },
  { value: "settings", label: "הגדרות", icon: Settings2 },
  { value: "updates", label: "עדכונים", icon: Download },
  { value: "logs", label: "יומן", icon: Activity },
]

const EVENTS_PAGE_SIZE = 100
const EVENTS_MAX_LIMIT = 1000
// שורות שהמכשיר משחזר מהדיסק עם הפעלת הזרם החי, כדי שלא יתחיל ריק
const LOG_BACKFILL_LINES = 100

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const deviceId = id ?? ""
  const confirm = useConfirm()

  // הלשונית הפעילה יושבת ב-URL, כדי שרענון או שיתוף קישור ינחתו באותו מקום
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get("tab")
  const tab = TABS.some((t) => t.value === tabParam) ? tabParam! : "overview"
  const setTab = (value: string) => setSearchParams({ tab: value }, { replace: true })

  const [device, setDevice] = React.useState<Device | null>(null)
  const [events, setEvents] = React.useState<DeviceEvent[] | null>(null)
  const [eventsHasMore, setEventsHasMore] = React.useState(false)
  const [eventsLoadingMore, setEventsLoadingMore] = React.useState(false)
  const [tailLines, setTailLines] = React.useState<string[] | null>(null)
  const [streamedLogs, setStreamedLogs] = React.useState<LogLine[]>([])
  const [streaming, setStreaming] = React.useState(false)
  const [busy, setBusy] = React.useState(false)

  // צילום ושיקוף מסך. הפריים החי מגיע דרך זרם ה-SSE ולכן יושב כאן ולא בכרטיס.
  const [screenshot, setScreenshot] = React.useState<Screenshot | null>(null)
  const [frame, setFrame] = React.useState<ScreenFrame | null>(null)
  const [mirroring, setMirroring] = React.useState(false)
  const [projectionGranted, setProjectionGranted] = React.useState(false)

  // חלונית מעקב אחרי עדכון/התקנה
  const [monitor, setMonitor] = React.useState<{ title: string; startedAt: number } | null>(null)
  const [monitorLogs, setMonitorLogs] = React.useState<LogLine[]>([])
  const [monitorEvents, setMonitorEvents] = React.useState<MonitorEvent[]>([])
  // ref ולא state: נקרא מתוך ה-callback של ה-SSE, שלא נוצר מחדש בכל רינדור
  const monitorActive = React.useRef(false)

  const loadDevice = React.useCallback(async () => {
    try {
      const d = await api<Device>(`/api/devices/${encodeURIComponent(deviceId)}`)
      setDevice(d)
    } catch (err) {
      toast.error(`טעינה נכשלה: ${(err as Error).message}`)
    }
  }, [deviceId])

  const fetchEvents = React.useCallback((params: { limit: number; before?: number }) => {
    const qs = new URLSearchParams({ limit: String(params.limit) })
    if (params.before) qs.set("before", String(params.before))
    return api<DeviceEvent[]>(`/api/devices/${encodeURIComponent(deviceId)}/events?${qs}`)
  }, [deviceId])

  // כמה אירועים כבר נטענו - כדי שרענון לא יקטין בחזרה למנה אחת
  const loadedCount = React.useRef(0)

  const loadEvents = React.useCallback(async () => {
    const limit = Math.min(Math.max(loadedCount.current, EVENTS_PAGE_SIZE), EVENTS_MAX_LIMIT)
    try {
      const rows = await fetchEvents({ limit })
      setEvents(rows)
      loadedCount.current = rows.length
      setEventsHasMore(rows.length >= limit)
    } catch (err) {
      toast.error(`לא ניתן לטעון אירועים: ${(err as Error).message}`)
    }
  }, [fetchEvents])

  const loadMoreEvents = React.useCallback(async () => {
    const last = events?.[events.length - 1]
    if (!last) return
    setEventsLoadingMore(true)
    try {
      const rows = await fetchEvents({ limit: EVENTS_PAGE_SIZE, before: last.id })
      setEvents((prev) => [...(prev ?? []), ...rows])
      loadedCount.current += rows.length
      setEventsHasMore(rows.length === EVENTS_PAGE_SIZE)
    } catch (err) {
      toast.error(`לא ניתן לטעון אירועים: ${(err as Error).message}`)
    } finally {
      setEventsLoadingMore(false)
    }
  }, [events, fetchEvents])

  React.useEffect(() => {
    loadedCount.current = 0
    setEvents(null)
    setEventsHasMore(false)
  }, [deviceId])

  React.useEffect(() => {
    loadDevice()
    loadEvents()
  }, [loadDevice, loadEvents])

  // עדכון סטטוס אוטומטי בכניסה לעמוד (וכאשר המכשיר חוזר ל-online).
  // SSE יעדכן את התצוגה ברגע שמגיעה התגובה מהמכשיר.
  const autoRefreshedFor = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!device?.online) {
      autoRefreshedFor.current = null
      return
    }
    if (autoRefreshedFor.current === deviceId) return
    autoRefreshedFor.current = deviceId
    sendCommand(deviceId, "get_status").catch(() => {})
  }, [deviceId, device?.online])

  useDevicesStream(React.useCallback((event, data) => {
    const d = data as { deviceId?: string }
    if (d?.deviceId !== deviceId) return
    if (event === "device:frame") {
      // חובה לצאת כאן: בהמשך הפונקציה יש loadDevice(), ובקצב 3 פריימים
      // בשנייה זה היה מייצר שלוש קריאות שרת בשנייה לכל צופה.
      setFrame((data as { frame?: ScreenFrame }).frame ?? null)
      return
    }
    if (event === "device:log") {
      const payload = (data as { log?: LogLine }).log || {}
      setStreamedLogs((prev) => [...prev.slice(-499), payload])
      if (monitorActive.current) {
        setMonitorLogs((prev) => [...prev.slice(-299), payload])
      }
      return
    }
    if (event === "device:event") {
      const { event: name, data: payload } = data as { event?: string; data?: UpdateEventData }
      if (monitorActive.current && name?.startsWith("update_")) {
        setMonitorEvents((prev) => [...prev, { event: name, data: payload ?? {}, at: Date.now() }])
      }
      if (name === "projection_consent") {
        const p = payload as { granted?: boolean; mirror?: MirrorStatus }
        const granted = !!p?.granted
        setProjectionGranted(granted)
        // The device starts mirroring itself on approval - on Android 14+ the
        // consent token is single-use, so waiting for a second command would risk
        // it expiring in between.
        if (p?.mirror?.streaming) setMirroring(true)
        toast[granted ? "success" : "error"](
          granted
            ? (p?.mirror?.streaming ? "האישור התקבל - השיקוף התחיל" : "האישור לשיקוף מסך התקבל מהטאבלט")
            : "האישור לשיקוף מסך נדחה על הטאבלט"
        )
      }
      if (name === "mirror_stopped") {
        setMirroring(false)
        setFrame(null)
      }
    }
    loadDevice()
  }, [deviceId, loadDevice]))

  /**
   * מדליק את הזרם החי, מנקה את החלונית ופותח אותה - נקרא לפני שליחת פקודת
   * עדכון, כדי שאף שורת לוג של התהליך לא תתפספס. אם הזרם כבר היה דלוק,
   * הסגירה לא תכבה אותו.
   */
  const beginMonitor = React.useCallback(async (title: string) => {
    setMonitorLogs([])
    setMonitorEvents([])
    monitorActive.current = true
    setMonitor({ title, startedAt: Date.now() })
    if (streaming) return
    try {
      // backfill: 0 - רוצים רק את מה שקורה מכאן והלאה
      await sendCommand(deviceId, "stream_logs", { enable: true, backfill: 0 })
      setStreaming(true)
    } catch (err) {
      toast.error(`לא ניתן להפעיל זרם לוגים: ${(err as Error).message}`)
    }
  }, [deviceId, streaming])

  // סוגרת רק את החלונית. הזרם החי נשאר דלוק בכוונה: אחרי ההתקנה האפליקציה
  // עולה מחדש ומתחברת, וכך רואים בכרטיס הלוגים גם את העלייה של הגרסה החדשה.
  const closeMonitor = React.useCallback(() => {
    monitorActive.current = false
    setMonitor(null)
  }, [])

  async function runCommand<T = unknown>(label: string, type: Parameters<typeof sendCommand>[1], params?: Record<string, unknown>) {
    setBusy(true)
    try {
      const data = await sendCommand<T>(deviceId, type, params)
      toast.success(`${label} - הצליח`)
      return data
    } catch (err) {
      toast.error(`${label} - נכשל: ${describeCommandError(err)}`)
      throw err
    } finally {
      setBusy(false)
      loadEvents()
    }
  }

  async function refreshStatus() {
    await runCommand("רענון סטטוס", "get_status").catch(() => {})
    await loadDevice()
  }

  async function ping() {
    const data = await runCommand<{ pong: number }>("Ping", "ping").catch(() => null)
    if (data?.pong) {
      toast.message(`Pong: ${new Date(data.pong).toLocaleString("he-IL")}`)
    }
  }

  async function hardRefresh() {
    const ok = await confirm({
      title: "לרענן את ה-WebView?",
      description: "המכשיר יטען מחדש את הדף המוצג. איסוף מתנות שנמצא באמצע עלול להיקטע.",
      confirmLabel: "רענן",
    })
    if (!ok) return
    await runCommand("רענון WebView", "hard_refresh").catch(() => {})
  }

  async function restartApp() {
    const ok = await confirm({
      title: "להפעיל מחדש את האפליקציה?",
      description: "האפליקציה תיסגר ותיפתח מחדש. הטאבלט עצמו לא יאותחל.",
      confirmLabel: "הפעל מחדש",
      destructive: true,
    })
    if (!ok) return
    await runCommand("הפעלה מחדש", "restart_app").catch(() => {})
  }

  async function reboot() {
    const ok = await confirm({
      title: "לאתחל את הטאבלט?",
      description: "המכשיר ייכבה ויעלה מחדש, ויהיה מנותק לכמה דקות.",
      confirmLabel: "אתחל",
      destructive: true,
    })
    if (!ok) return
    await runCommand("איתחול טאבלט", "reboot").catch(() => {})
  }

  async function fetchTail(lines = 200) {
    const data = await runCommand<{ lines: string[] }>("שליפת לוגים", "tail_logs", { lines }).catch(() => null)
    if (data?.lines) setTailLines(data.lines)
  }

  async function toggleStream() {
    const next = !streaming
    // מנקים לפני ההפעלה כדי שה-backfill לא ייערם על שורות מהזרם הקודם
    if (next) setStreamedLogs([])
    await runCommand(
      next ? "הפעלת זרם לוגים" : "כיבוי זרם לוגים",
      "stream_logs",
      next ? { enable: true, backfill: LOG_BACKFILL_LINES } : { enable: false },
    ).catch(() => null)
    setStreaming(next)
  }

  if (!device) {
    return <div className="p-4 text-sm text-muted-foreground">טוען…</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon-sm">
            <Link to="/">
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{device.displayLabel}</h1>
            <p className="font-mono text-xs text-muted-foreground">{device.deviceId}</p>
          </div>
        </div>
        <Badge variant={device.online ? "success" : "outline"}>
          {device.online ? (
            <>
              <Wifi className="size-3" />
              מחובר {device.lastProtocol ? `(${device.lastProtocol.toUpperCase()})` : ""}
            </>
          ) : (
            <>
              <WifiOff className="size-3" />
              לא מחובר
            </>
          )}
        </Badge>
      </div>

      <p className="text-sm text-muted-foreground">
        לא בטוח מה כפתור עושה?{" "}
        <Link to="/help#device-overview" className="underline hover:text-foreground">
          המדריך למשתמש
        </Link>{" "}
        מסביר כל לשונית וכל פעולה.
      </p>

      <DeviceActions
        device={device}
        busy={busy}
        onRefreshStatus={refreshStatus}
        onPing={ping}
        onHardRefresh={hardRefresh}
        onRestartApp={restartApp}
        onReboot={reboot}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {TABS.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value}>
              <Icon />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <LiveDomainCard deviceId={device.deviceId} online={device.online} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DeviceStatusCard device={device} />
            <DeviceMetadataCard device={device} onSaved={loadDevice} />
          </div>
        </TabsContent>

        <TabsContent value="display">
          <UrlCard deviceId={device.deviceId} online={device.online} runCommand={runCommand} />
          <DomainsCard deviceId={device.deviceId} online={device.online} runCommand={runCommand} />
        </TabsContent>

        <TabsContent value="screen">
          <ScreenCard
            deviceId={device.deviceId}
            online={device.online}
            runCommand={runCommand}
            screenshot={screenshot}
            onScreenshot={setScreenshot}
            frame={frame}
            onClearFrame={() => setFrame(null)}
            mirroring={mirroring}
            onMirroring={setMirroring}
            projectionGranted={projectionGranted}
            onProjectionGranted={setProjectionGranted}
          />
        </TabsContent>

        <TabsContent value="settings">
          <DeviceSettingsCard
            deviceId={device.deviceId}
            online={device.online}
            runCommand={runCommand}
          />
          <RemoteConfigCard runCommand={runCommand} />
          <DangerZoneCard device={device} />
        </TabsContent>

        <TabsContent value="updates">
          <UpdatesCard
            deviceId={device.deviceId}
            online={device.online}
            runCommand={runCommand}
            onInstalled={loadEvents}
            beginMonitor={beginMonitor}
          />
        </TabsContent>

        <TabsContent value="logs">
          <LogsCard
            tailLines={tailLines}
            streaming={streaming}
            streamedLogs={streamedLogs}
            onFetchTail={fetchTail}
            onClearStream={() => setStreamedLogs([])}
            onToggleStream={toggleStream}
            disabled={!device.online}
          />
          <EventsCard
            events={events}
            hasMore={eventsHasMore}
            loadingMore={eventsLoadingMore}
            onRefresh={loadEvents}
            onLoadMore={loadMoreEvents}
          />
        </TabsContent>
      </Tabs>

      {/* מחוץ ללשוניות: חלון המעקב צריך לשרוד גם מעבר לשונית בזמן עדכון */}
      {monitor ? (
        <UpdateMonitorDialog
          title={monitor.title}
          logs={monitorLogs}
          events={monitorEvents}
          onClose={closeMonitor}
        />
      ) : null}
    </div>
  )
}

const MAX_FPS = 30
const DEFAULT_FPS = { window: 8, projection: 15 } as const

/** מצבי השיקוף. "window" עובד תמיד; "projection" דורש אישור אנושי על הטאבלט. */
const MIRROR_MODES = [
  {
    value: "window" as const,
    label: "חלון האפליקציה",
    hint: "ללא הרשאות, פועל גם ללא נוכחות ליד הטאבלט. מכסה את כל מה שהאפליקציה מציירת.",
  },
  {
    value: "projection" as const,
    label: "מסך מלא אמיתי",
    hint: "כולל שורת סטטוס, מקלדת ואפליקציות אחרות. דורש אישור חד-פעמי על הטאבלט.",
  },
]

function ScreenCard({
  deviceId, online, runCommand,
  screenshot, onScreenshot,
  frame, onClearFrame,
  mirroring, onMirroring,
  projectionGranted, onProjectionGranted,
}: {
  deviceId: string
  online: boolean
  runCommand: RunCommand
  screenshot: Screenshot | null
  onScreenshot: (s: Screenshot | null) => void
  frame: ScreenFrame | null
  onClearFrame: () => void
  mirroring: boolean
  onMirroring: (v: boolean) => void
  projectionGranted: boolean
  onProjectionGranted: (v: boolean) => void
}) {
  const [mode, setMode] = React.useState<"window" | "projection">("window")
  // Projection frames are cheap on the device, window frames cost a full readback
  // plus a JPEG encode each - so the two modes carry different sensible defaults.
  const [fps, setFps] = React.useState<number>(DEFAULT_FPS.window)
  const [fpsTouched, setFpsTouched] = React.useState(false)
  const [busy, setBusy] = React.useState(false)

  // קצב הפריימים בפועל, לפי מרווחי ההגעה. מפריד בין "השיקוף תקוע" לבין
  // "השיקוף רץ אבל הרשת איטית" - שני מצבים שנראים אותו דבר בלי המדד הזה.
  const arrivals = React.useRef<number[]>([])
  const [liveFps, setLiveFps] = React.useState(0)
  React.useEffect(() => {
    if (!frame) return
    const now = Date.now()
    arrivals.current = [...arrivals.current, now].filter((t) => now - t < 5000)
    const span = arrivals.current.length > 1
      ? now - arrivals.current[0]
      : 0
    setLiveFps(span > 0 ? Math.round(((arrivals.current.length - 1) / span) * 1000 * 10) / 10 : 0)
  }, [frame])

  // סטטוס השיקוף נקרא פעם אחת בכניסה ללשונית, כדי שרענון דף בזמן שיקוף
  // פעיל לא יציג "כבוי" בזמן שהמכשיר עדיין משדר.
  React.useEffect(() => {
    if (!online) return
    let cancelled = false
    sendCommand<MirrorStatus>(deviceId, "get_mirror_status")
      .then((s) => {
        if (cancelled || !s) return
        onMirroring(!!s.streaming)
        onProjectionGranted(!!s.projectionGranted)
        if (s.streaming && s.mode) setMode(s.mode)
        if (s.fps) setFps(s.fps)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [deviceId, online])

  /**
   * Tells the device a viewer is still here. The device stops mirroring on its own
   * after ~30s of silence, so this is what makes a closed tab, a crashed browser or
   * a dropped connection release the tablet instead of leaving it capturing forever.
   */
  React.useEffect(() => {
    if (!mirroring || !online) return
    const id = window.setInterval(() => {
      sendCommand(deviceId, "mirror_keepalive").catch(() => {})
    }, 10000)
    return () => window.clearInterval(id)
  }, [mirroring, online, deviceId])

  /**
   * Unmount - switching tab or leaving the device page - stops the mirror straight
   * away rather than waiting for the keepalive to lapse. Reads the live value from a
   * ref so the cleanup does not re-run (and stop the mirror) on every state change.
   */
  const mirroringRef = React.useRef(mirroring)
  mirroringRef.current = mirroring
  React.useEffect(() => {
    return () => {
      if (mirroringRef.current) {
        sendCommand(deviceId, "stop_mirror").catch(() => {})
      }
    }
  }, [deviceId])

  async function capture() {
    setBusy(true)
    try {
      const shot = await runCommand<Screenshot>("צילום מסך", "screenshot", {
        maxWidth: 1080, quality: 75, closeAd: true,
      })
      onScreenshot(shot)
      if (shot?.adWasClosed) toast.info("פרסומת הייתה מוצגת ונסגרה לפני הצילום")
    } catch { /* runCommand כבר הודיע */ } finally { setBusy(false) }
  }

  async function requestConsent() {
    setBusy(true)
    try {
      const r = await runCommand<{ granted: boolean; prompted: boolean }>(
        "בקשת אישור שיקוף", "request_projection_consent"
      )
      if (r?.granted) onProjectionGranted(true)
      else if (r?.prompted) {
        toast.info("דיאלוג האישור מוצג כעת על הטאבלט - יש לאשר שם. השיקוף יתחיל מיד לאחר האישור")
      }
    } catch { /* runCommand כבר הודיע */ } finally { setBusy(false) }
  }

  async function hideAd() {
    setBusy(true)
    try {
      const r = await runCommand<{ adWasDisplayed: boolean }>("סגירת פרסומת", "hide_ad")
      toast[r?.adWasDisplayed ? "success" : "info"](
        r?.adWasDisplayed ? "הפרסומת נסגרה" : "לא הוצגה פרסומת כרגע"
      )
    } catch { /* runCommand כבר הודיע */ } finally { setBusy(false) }
  }

  async function toggleMirror() {
    setBusy(true)
    try {
      if (mirroring) {
        await runCommand("עצירת שיקוף", "stop_mirror")
        onMirroring(false)
        onClearFrame()
        return
      }
      const s = await runCommand<MirrorStatus>("הפעלת שיקוף", "start_mirror", {
        mode, fps, maxWidth: 720, quality: 50,
      })
      if (s?.error) {
        toast.error(COMMAND_ERRORS[s.error] ?? s.error)
        if (s.error === "projection_consent_required") onProjectionGranted(false)
        return
      }
      onMirroring(!!s?.streaming)
    } catch { /* runCommand כבר הודיע */ } finally { setBusy(false) }
  }

  function download() {
    if (!screenshot) return
    const a = document.createElement("a")
    a.href = `data:image/jpeg;base64,${screenshot.image}`
    a.download = `screenshot-${deviceId}-${screenshot.capturedAt}.jpg`
    a.click()
  }

  const needsConsent = mode === "projection" && !projectionGranted

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Camera className="size-4" />
              צילום מסך
            </CardTitle>
            <CardDescription>
              מצלם את מה שמוצג כרגע על הטאבלט. אם מוצגת פרסומת היא נסגרת אוטומטית לפני הצילום,
              כדי שהתמונה תראה את התוכן ולא את הפרסומת.
            </CardDescription>
          </div>
          <div className="flex shrink-0 gap-2">
            {screenshot && (
              <Button size="xs" variant="ghost" onClick={download}>
                <Download className="size-3" />
                הורד
              </Button>
            )}
            <Button size="xs" onClick={capture} disabled={!online || busy}>
              <Camera className={busy ? "size-3 animate-pulse" : "size-3"} />
              צלם עכשיו
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!online ? (
            <div className="text-sm text-muted-foreground">המכשיר לא מחובר - לא ניתן לצלם.</div>
          ) : !screenshot ? (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              עדיין לא צולם מסך. לחץ "צלם עכשיו".
            </div>
          ) : (
            <div className="space-y-2">
              <img
                src={`data:image/jpeg;base64,${screenshot.image}`}
                alt="צילום מסך של המכשיר"
                className="mx-auto max-h-[60vh] w-auto max-w-full rounded-md border"
              />
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">{screenshot.width}×{screenshot.height}</Badge>
                <span>{formatRelative(new Date(screenshot.capturedAt).toISOString())}</span>
                {screenshot.adWasClosed && <Badge variant="outline">פרסומת נסגרה לפני הצילום</Badge>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <MonitorPlay className="size-4" />
              שיקוף מסך חי
              {mirroring && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-emerald-600 dark:text-emerald-400">
                  <span className="relative flex size-2">
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                    <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                  </span>
                  משדר {liveFps > 0 ? `· ${liveFps} fps` : ""}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              זרם תמונות חי מהטאבלט. בשונה מהצילום החד-פעמי, השיקוף מציג את המסך כמות שהוא -
              כולל פרסומת אם היא מוצגת.
            </CardDescription>
          </div>
          <div className="flex shrink-0 gap-2">
            {/* שימושי בעיקר בזמן שיקוף, אבל עובד בכל מצב ובשני מצבי השיקוף */}
            <Button size="xs" variant="outline" onClick={hideAd} disabled={!online || busy}>
              <EyeOff className="size-3" />
              סגור פרסומת
            </Button>
            <Button
              size="xs"
              variant={mirroring ? "destructive" : "default"}
              onClick={toggleMirror}
              disabled={!online || busy || (needsConsent && !mirroring)}
            >
              {mirroring ? <Square className="size-3" /> : <MonitorPlay className="size-3" />}
              {mirroring ? "עצור" : "הפעל"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {MIRROR_MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                disabled={mirroring}
                onClick={() => {
                  setMode(m.value)
                  // Follows the mode until the operator sets a rate of their own.
                  if (!fpsTouched) setFps(DEFAULT_FPS[m.value])
                }}
                className={[
                  "rounded-md border p-3 text-start transition",
                  mode === m.value ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                  mirroring ? "cursor-not-allowed opacity-60" : "",
                ].join(" ")}
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  {m.label}
                  {m.value === "projection" && projectionGranted && (
                    <Badge variant="secondary" className="gap-1">
                      <ShieldCheck className="size-3" />
                      מאושר
                    </Badge>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{m.hint}</div>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="mirror-fps" className="text-xs">פריימים בשנייה</Label>
              <Input
                id="mirror-fps"
                type="number"
                min={1}
                max={MAX_FPS}
                value={fps}
                disabled={mirroring}
                onChange={(e) => {
                  setFpsTouched(true)
                  setFps(Math.max(1, Math.min(MAX_FPS, Number(e.target.value) || DEFAULT_FPS[mode])))
                }}
                className="w-24"
              />
            </div>
            {needsConsent && (
              <Button size="sm" variant="outline" onClick={requestConsent} disabled={!online || busy}>
                <ShieldCheck className="size-3" />
                בקש אישור מהטאבלט
              </Button>
            )}
          </div>

          {needsConsent && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
              שיקוף מסך מלא מחייב אישור אנושי חד-פעמי על הטאבלט - אנדרואיד לא מאפשר לאשר אותו
              מרחוק, גם לא לאפליקציה שמותקנת כבעלים של המכשיר. באנדרואיד 14 ומעלה האישור תקף
              לסשן אחד בלבד ויידרש שוב בהפעלה הבאה. אם אין מי שיאשר ליד הטאבלט, השתמש במצב
              "חלון האפליקציה".
            </div>
          )}

          {mirroring && !frame ? (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              ממתין לפריים הראשון…
            </div>
          ) : frame ? (
            <div className="space-y-2">
              <img
                src={`data:image/jpeg;base64,${frame.image}`}
                alt="שיקוף חי של מסך המכשיר"
                className="mx-auto max-h-[60vh] w-auto max-w-full rounded-md border"
              />
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">{frame.width}×{frame.height}</Badge>
                <span>פריים #{frame.seq}</span>
                <span>{frame.source === "projection" ? "מסך מלא" : "חלון האפליקציה"}</span>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}

function LiveDomainCard({ deviceId, online }: { deviceId: string; online: boolean }) {
  const live = useLiveDomain(deviceId, online, 30000)

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <Globe className="size-4" />
            מוצג כעת על המסך
            {online && (
              <span className="inline-flex items-center gap-1 text-xs font-normal text-emerald-600 dark:text-emerald-400">
                <span className="relative flex size-2">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                  <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                </span>
                חי
              </span>
            )}
          </CardTitle>
          <CardDescription>
            הכתובת שמוצגת ממש עכשיו בדפדפן הפנימי של הטאבלט. נשאלת מהמכשיר אוטומטית כל 30 שניות.
          </CardDescription>
        </div>
        <Button size="xs" variant="ghost" onClick={live.refresh} disabled={!online || live.loading}>
          <RefreshCw className={live.loading ? "size-3 animate-spin" : "size-3"} />
          רענן
        </Button>
      </CardHeader>
      <CardContent>
        {!online ? (
          <div className="text-sm text-muted-foreground">המכשיר לא מחובר - לא ניתן לקרוא את הכתובת המוצגת.</div>
        ) : !live.loaded && live.error ? (
          <div className="text-sm text-amber-600">לא ניתן לקרוא את הכתובת המוצגת כעת. ייתכן שהמכשיר לא תומך בפקודה.</div>
        ) : !live.loaded ? (
          <div className="text-sm text-muted-foreground">טוען…</div>
        ) : (
          <div className="space-y-1">
            {live.noActivity ? (
              // המכשיר מחובר, אבל אין Activity רשומה - כל הפקודות שתלויות
              // ב-WebView ייכשלו עד שהאפליקציה תופעל מחדש
              <>
                <div className="text-2xl font-semibold tracking-tight text-amber-600 dark:text-amber-400">
                  -
                </div>
                <div className="text-sm text-amber-600 dark:text-amber-400">
                  מסך האפליקציה אינו רשום לשליטה מרחוק. רענון WebView וקריאת הכתובת ייכשלו.
                  הפעל מחדש את האפליקציה (כרטיס "הגדרות האפליקציה" ← "הפעל מחדש").
                </div>
              </>
            ) : live.url == null ? (
              // המכשיר ענה בהצלחה אבל אין עמוד טעון ב-WebView (data.url = null)
              <>
                <div className="text-2xl font-semibold tracking-tight text-muted-foreground">-</div>
                <div className="text-sm text-muted-foreground">אין עמוד טעון ב-WebView של המכשיר.</div>
              </>
            ) : (
              <>
                <div className="text-2xl font-semibold tracking-tight" dir="ltr">
                  {live.domain || "-"}
                </div>
                <a
                  href={live.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block break-all font-mono text-xs text-muted-foreground hover:text-primary hover:underline"
                  dir="ltr"
                >
                  {live.url}
                </a>
              </>
            )}
            <div className="text-xs text-muted-foreground">
              עודכן {formatRelative(live.updatedAt ? new Date(live.updatedAt).toISOString() : null)}
              {live.error ? <span className="text-amber-600"> · הרענון האחרון נכשל, מנסים שוב</span> : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function DeviceActions({
  device, busy, onRefreshStatus, onPing, onHardRefresh, onRestartApp, onReboot,
}: {
  device: Device
  busy: boolean
  onRefreshStatus: () => void
  onPing: () => void
  onHardRefresh: () => void
  onRestartApp: () => void
  onReboot: () => void
}) {
  const offlineNote = !device.online && (
    <div className="text-xs text-muted-foreground">המכשיר לא מחובר - הפקודות יחזרו עם שגיאה.</div>
  )
  return (
    <Card>
      <CardContent className="p-4">
        {/* בטלפון: רשת של שני כפתורים בשורה במקום שורה אחת שנדחסת או נשברת בכיעור */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <Button
            size="sm"
            className="w-full sm:w-auto"
            onClick={onRefreshStatus}
            disabled={busy || !device.online}
            title="מבקש מהטאבלט דיווח מצב עדכני: סוללה, זיכרון, אחסון וגרסה. פעולה בטוחה."
          >
            <RefreshCw className="size-3.5" />
            רענן סטטוס
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={onPing}
            disabled={busy || !device.online}
            title="בדיקת חיים: האם הטאבלט עונה וכמה מהר. לא משנה כלום במכשיר."
          >
            <Cpu className="size-3.5" />
            Ping
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={onHardRefresh}
            disabled={busy || !device.online}
            title="טוען מחדש את הדף שמוצג על מסך הטאבלט. איסוף מתנה שבאמצע ייקטע."
          >
            <RotateCw className="size-3.5" />
            רענון WebView
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={onRestartApp}
            disabled={busy || !device.online}
            title="סוגר ופותח את האפליקציה על הטאבלט. הטאבלט עצמו לא מאותחל."
          >
            <RotateCcw className="size-3.5" />
            הפעל מחדש אפליקציה
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="w-full sm:w-auto"
            onClick={onReboot}
            disabled={busy || !device.online}
            title="מכבה ומדליק את הטאבלט עצמו. הוא יהיה מנותק לכמה דקות."
          >
            <Power className="size-3.5" />
            אתחל טאבלט
          </Button>

          <div className="col-span-2 sm:ms-auto">
            {offlineNote || (
              <div className="text-xs text-muted-foreground">
                פעולות מהירות על הטאבלט. העבר עכבר על כפתור להסבר.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function DeviceMetadataCard({ device, onSaved }: { device: Device; onSaved: () => void }) {
  const [customLabel, setCustomLabel] = React.useState(device.customLabel || "")
  const [notes, setNotes] = React.useState(device.notes || "")
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    setCustomLabel(device.customLabel || "")
    setNotes(device.notes || "")
  }, [device.deviceId, device.customLabel, device.notes])

  async function save() {
    setSaving(true)
    try {
      await api(`/api/devices/${encodeURIComponent(device.deviceId)}`, {
        method: "PATCH",
        json: { customLabel: customLabel || null, notes: notes || null },
      })
      toast.success("נשמר")
      onSaved()
    } catch (err) {
      toast.error(`שמירה נכשלה: ${(err as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>פרטי מכשיר</CardTitle>
        <CardDescription>
          תווית והערות שנשמרות בשרת בלבד ולא נשלחות לטאבלט. התווית מחליפה את השם שהמכשיר מדווח על
          עצמו - למשל "כניסה - אולם א'".
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="תווית מקומית">
          <Input
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            placeholder={device.reportedLabel || "ללא"}
          />
        </Field>
        <Field label="תווית מהמכשיר">
          <div className="text-sm">{device.reportedLabel || "-"}</div>
        </Field>
        <Field label="הערות">
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="טקסט חופשי"
          />
        </Field>
        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
          <Info label="התחבר לאחרונה" value={formatAbsolute(device.lastConnectedAt)} />
          <Info label="ניתק לאחרונה" value={formatAbsolute(device.lastDisconnectedAt)} />
          <Info label="פרוטוקול" value={device.lastProtocol || "-"} />
          <Info label="כתובת IP" value={device.lastRemoteAddr || "-"} />
        </div>
        <div>
          <Button size="sm" onClick={save} disabled={saving}>
            <Save className="size-3.5" />
            שמור
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * מנותק מהחשמל הרבה מעבר להשהיה, ועדיין לא במצב חיסכון - הסימן לתקלה שהמנגנון
 * החדש אמור לרפא (שידור שאבד). מרווח של פי 3 מההשהיה כדי לא להתריע על מכשיר
 * שפשוט עוד סופר.
 */
function isPowerSaverStuck(ps: NonNullable<DeviceStatus["powerSaver"]>) {
  if (!ps.enabled || ps.inSaveMode || !ps.onBattery) return false
  const delayMs = (ps.delaySec ?? 60) * 1000
  return (ps.unpluggedForMillis ?? 0) > Math.max(delayMs * 3, delayMs + 120_000)
}

/** תרגום סיבות השחזור שהמכשיר מדווח - הן מגיעות כמזהים באנגלית. */
function kioskGuardReasonText(reason: string | null | undefined): string {
  if (!reason) return "-"
  const map: Record<string, string> = {
    "not-device-owner": "האפליקציה אינה Device Owner",
    "kiosk-pref-off": "מתג הקיוסק כבוי",
    "not-home-app": "האפליקציה אינה אפליקציית הבית (HOME)",
    "lock-task-none": "המכשיר לא נעול על האפליקציה",
    "lock-task-pinned": "המסך מוצמד ידנית ולא במצב קיוסק",
    "foreground-none": "האפליקציה ברקע",
    "power-save-mode": "מצב חיסכון בחשמל",
    "screen-mirroring": "שיקוף מסך פעיל",
    "install-in-progress": "התקנה בתהליך",
    "projection-consent": "בקשת אישור שיקוף",
  }
  if (map[reason]) return map[reason]
  if (reason.startsWith("foreground-")) return `מסך אחר בחזית: ${reason.slice("foreground-".length)}`
  return reason
}

function lockTaskStateText(state: string | null | undefined): string {
  if (!state) return "-"
  const map: Record<string, string> = {
    locked: "נעול (קיוסק)",
    none: "לא נעול",
    pinned: "הצמדת מסך ידנית",
    unknown: "לא ידוע",
  }
  return map[state] ?? state
}

/**
 * המועד עבר מזמן והשחזור עדיין לא תפס. שלוש כשלונות זה כבר לא "עוד רגע" - זה
 * מכשיר שצריך להסתכל עליו. deferReason מוציא אותו מכלל זה: שם ההשהיה מכוונת.
 */
function isKioskGuardStuck(kg: NonNullable<DeviceStatus["kioskGuard"]>) {
  if (!kg.enabled || kg.healthy || kg.deferReason) return false
  if ((kg.failCount ?? 0) >= 3) return true
  const remaining = kg.remainingMillis ?? 0
  return kg.armed === true && remaining < -120_000
}

function DeviceStatusCard({ device }: { device: Device }) {
  const s = device.lastStatus
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="space-y-1.5">
          <CardTitle>סטטוס אחרון</CardTitle>
          <CardDescription>
            תמונת המצב האחרונה שהתקבלה מהטאבלט. אם היא נראית ישנה - לחץ "רענן סטטוס" למעלה.
          </CardDescription>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">{formatRelative(device.lastStatusAt)}</span>
      </CardHeader>
      <CardContent className="space-y-3">
        {!s ? (
          <div className="text-sm text-muted-foreground">אין נתוני סטטוס.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Info label="מודל" value={`${s.manufacturer || ""} ${s.model || ""}`.trim() || "-"} />
              <Info label="גרסת אפליקציה" value={s.appVersion || "-"} />
              <Info label="גרסת Android" value={`${s.androidRelease || "-"} (SDK ${s.androidSdk ?? "-"})`} />
              <Info label="זמן פעולה" value={formatUptime(s.elapsedRealtimeMillis)} />
            </div>
            <Separator />
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm font-medium">
                <BatteryIcon percent={s.battery?.percent} charging={s.battery?.charging} />
                סוללה
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Info label="רמה" value={s.battery?.percent != null ? `${s.battery.percent.toFixed(0)}%` : "-"} />
                <Info label="מצב טעינה" value={s.battery?.plugged ? s.battery.plugged : (s.battery?.charging ? "טוען" : "מנותק")} />
                <Info label="טמפרטורה" value={s.battery?.temperatureCelsius != null ? `${s.battery.temperatureCelsius.toFixed(1)}°C` : "-"} icon={<Thermometer className="size-3" />} />
                <Info label="מתח" value={s.battery?.voltageMilliVolts ? `${s.battery.voltageMilliVolts} mV` : "-"} />
              </div>
            </div>
            <Separator />
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm font-medium">
                <HardDrive className="size-4" />
                זיכרון ואחסון
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Info label="RAM פנוי" value={formatBytes(s.memory?.availableRamBytes)} />
                <Info label="RAM סך הכל" value={formatBytes(s.memory?.totalRamBytes)} />
                <Info label="אחסון פנוי" value={formatBytes(s.storage?.internalAvailableBytes)} />
                <Info label="אחסון סך הכל" value={formatBytes(s.storage?.internalTotalBytes)} />
              </div>
            </div>
            {s.powerSaver ? (
              <>
                <Separator />
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Leaf className="size-4" />
                    חיסכון בחשמל
                    {s.powerSaver.inSaveMode ? (
                      <Badge variant="default">במצב חיסכון עכשיו</Badge>
                    ) : s.powerSaver.enabled ? (
                      <Badge variant="outline">פעיל, לא בחיסכון</Badge>
                    ) : (
                      <Badge variant="outline">כבוי</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <Info
                      label="מקור מתח"
                      value={s.powerSaver.onBattery ? "סוללה (מנותק מהחשמל)" : "מחובר לחשמל"}
                    />
                    <Info
                      label="השהיה לפני כניסה"
                      value={s.powerSaver.delaySec != null ? `${s.powerSaver.delaySec} שניות` : "-"}
                    />
                    <Info
                      label="מנותק מהחשמל"
                      value={
                        s.powerSaver.unpluggedForMillis
                          ? formatUptime(s.powerSaver.unpluggedForMillis)
                          : "-"
                      }
                    />
                    <Info
                      label="לולאת בקרה"
                      value={s.powerSaver.watchdogRunning ? "רצה" : "לא רצה"}
                    />
                  </div>
                  {isPowerSaverStuck(s.powerSaver) ? (
                    <div className="rounded-md border border-amber-500 p-2 text-xs">
                      המכשיר מנותק מהחשמל הרבה מעבר להשהיה שהוגדרה, אבל לא נכנס למצב חיסכון.
                      לחץ "בדוק עכשיו" כדי לאלץ בדיקה מחדש - הלולאה במכשיר אמורה לתקן את זה
                      מעצמה תוך 30 שניות.
                    </div>
                  ) : null}
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={!device.online}
                    onClick={async () => {
                      try {
                        await sendCommand(device.deviceId, "power_saver_check")
                        toast.success("נשלחה בדיקת חיסכון - רענן סטטוס")
                      } catch (err) {
                        toast.error(`הבדיקה נכשלה: ${(err as Error).message}`)
                      }
                    }}
                  >
                    בדוק עכשיו
                  </Button>
                </div>
              </>
            ) : null}
            {s.kioskGuard ? (
              <>
                <Separator />
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <ShieldCheck className="size-4" />
                    שחזור אוטומטי לקיוסק
                    {!s.kioskGuard.enabled ? (
                      <Badge variant="outline">כבוי</Badge>
                    ) : s.kioskGuard.healthy ? (
                      <Badge variant="outline">קיוסק תקין</Badge>
                    ) : (
                      <Badge variant="destructive">מחוץ לקיוסק</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <Info
                      label="מצב"
                      value={
                        s.kioskGuard.healthy
                          ? "קיוסק תקין"
                          : kioskGuardReasonText(s.kioskGuard.unhealthyReason)
                      }
                    />
                    <Info
                      label="נעילת מסך"
                      value={lockTaskStateText(s.kioskGuard.lockTaskState ?? s.lockTaskState)}
                    />
                    <Info
                      label="זמן המתנה"
                      value={s.kioskGuard.delayMin != null ? `${s.kioskGuard.delayMin} דקות` : "-"}
                    />
                    <Info
                      label="שחזור בעוד"
                      value={
                        !s.kioskGuard.armed
                          ? "-"
                          : (s.kioskGuard.remainingMillis ?? 0) > 0
                            ? formatUptime(s.kioskGuard.remainingMillis)
                            : "ממתין לביצוע"
                      }
                    />
                    <Info label="אפליקציית בית" value={s.isHomeApp === false ? "לא" : s.isHomeApp === true ? "כן" : "-"} />
                    <Info
                      label="ניסיונות שכשלו"
                      value={s.kioskGuard.failCount ? String(s.kioskGuard.failCount) : "0"}
                    />
                  </div>
                  {s.kioskGuard.deferReason ? (
                    <div className="text-xs text-muted-foreground">
                      השחזור מושהה זמנית: {kioskGuardReasonText(s.kioskGuard.deferReason)}. הוא יתבצע
                      ברגע שהחסם ישתחרר.
                    </div>
                  ) : null}
                  {s.kioskGuard.isDeviceOwner === false ? (
                    <div className="rounded-md border border-destructive p-2 text-xs">
                      האפליקציה אינה Device Owner, ולכן השחזור האוטומטי אינו יכול לפעול כלל.
                    </div>
                  ) : isKioskGuardStuck(s.kioskGuard) ? (
                    <div className="rounded-md border border-amber-500 p-2 text-xs">
                      המועד לשחזור עבר והמכשיר עדיין לא חזר לקיוסק ({s.kioskGuard.failCount ?? 0}{" "}
                      ניסיונות). לחץ "החזר לקיוסק עכשיו" כדי לאלץ ניסיון מיידי; אם גם זה לא עוזר,
                      בדוק את הלוגים - המכשיר יאתחל את עצמו אחרי מספר ניסיונות.
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={!device.online}
                      onClick={async () => {
                        try {
                          await sendCommand(device.deviceId, "kiosk_guard_check")
                          toast.success("נשלחה בדיקת שחזור - רענן סטטוס")
                        } catch (err) {
                          toast.error(`הבדיקה נכשלה: ${(err as Error).message}`)
                        }
                      }}
                    >
                      בדוק עכשיו
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={!device.online}
                      onClick={async () => {
                        if (
                          !confirm(
                            "להחזיר את הטאבלט למצב קיוסק עכשיו? האפליקציה תופעל מחדש על התצוגה הראשית."
                          )
                        )
                          return
                        try {
                          await sendCommand(device.deviceId, "kiosk_guard_check", { force: true })
                          toast.success("נשלחה פקודת החזרה לקיוסק")
                        } catch (err) {
                          toast.error(`הפקודה נכשלה: ${(err as Error).message}`)
                        }
                      }}
                    >
                      החזר לקיוסק עכשיו
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

type RunCommand = <T>(l: string, t: Parameters<typeof sendCommand>[1], p?: Record<string, unknown>) => Promise<T>

type LoadState = "idle" | "loading" | "loaded" | "error"

function UrlCard({ deviceId, online, runCommand }: { deviceId: string; online: boolean; runCommand: RunCommand }) {
  const [url, setUrl] = React.useState("")
  const [state, setState] = React.useState<LoadState>("idle")

  const load = React.useCallback(async (silent: boolean) => {
    setState("loading")
    try {
      const d = silent
        ? await sendCommand<{ url: string }>(deviceId, "get_url")
        : await runCommand<{ url: string }>("שליפת כתובת אתחול", "get_url")
      setUrl(d?.url || "")
      setState("loaded")
    } catch (err) {
      setState("error")
      if (!silent) {
        toast.error(`שליפת כתובת נכשלה: ${(err as Error).message}`)
      }
    }
  }, [deviceId, runCommand])

  // טעינה אוטומטית של הערך הקיים מהמכשיר במעבר לעמוד / כשהמכשיר חוזר להיות מחובר
  React.useEffect(() => {
    setUrl("")
    setState("idle")
    if (online) load(true)
  }, [deviceId, online, load])

  async function save() {
    if (state !== "loaded") return toast.error("יש לטעון את הערך הנוכחי מהמכשיר לפני שמירה")
    if (!url.trim()) return toast.error("נדרשת כתובת")
    await runCommand("עדכון כתובת אתחול", "set_url", { url }).catch(() => {})
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="space-y-1.5">
          <CardTitle>כתובת אתחול (WebView)</CardTitle>
          <CardDescription>
            הכתובת שהטאבלט טוען כשהאפליקציה עולה - כלומר לאיזה אירוע הוא משויך. זו לא בהכרח הכתובת
            שמוצגת כרגע על המסך.
          </CardDescription>
        </div>
        <Button size="xs" variant="ghost" onClick={() => load(false)} disabled={!online || state === "loading"}>
          <RefreshCw className={state === "loading" ? "size-3 animate-spin" : "size-3"} />
          קרא מהמכשיר
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <Input
          placeholder={state === "loading" ? "טוען מהמכשיר…" : "https://gift.rsvpevents.co.il/events/..."}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={state === "loading" || (state !== "loaded" && online)}
          dir="ltr"
        />
        <LoadHint online={online} state={state} />
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={!online || state !== "loaded"}>
            <Link2 className="size-3.5" />
            עדכן כתובת
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function DomainsCard({ deviceId, online, runCommand }: { deviceId: string; online: boolean; runCommand: RunCommand }) {
  const [text, setText] = React.useState("")
  const [state, setState] = React.useState<LoadState>("idle")

  const load = React.useCallback(async (silent: boolean) => {
    setState("loading")
    try {
      const d = silent
        ? await sendCommand<{ domains: string[] }>(deviceId, "get_domains")
        : await runCommand<{ domains: string[] }>("שליפת דומיינים", "get_domains")
      setText((d?.domains || []).join("\n"))
      setState("loaded")
    } catch (err) {
      setState("error")
      if (!silent) {
        toast.error(`שליפת דומיינים נכשלה: ${(err as Error).message}`)
      }
    }
  }, [deviceId, runCommand])

  React.useEffect(() => {
    setText("")
    setState("idle")
    if (online) load(true)
  }, [deviceId, online, load])

  async function save() {
    if (state !== "loaded") return toast.error("יש לטעון את הרשימה הקיימת מהמכשיר לפני שמירה")
    const domains = text.split(/\s+/).map((s) => s.trim()).filter(Boolean)
    if (domains.length === 0) return toast.error("נדרש לפחות דומיין אחד")
    await runCommand("עדכון רשימת דומיינים", "set_domains", { domains }).catch(() => {})
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="space-y-1.5">
          <CardTitle>רשימת דומיינים מורשים</CardTitle>
          <CardDescription>
            רשימת ההיתר של הדפדפן הפנימי: לאן מותר לטאבלט לגלוש, דומיין אחד בכל שורה. אם הדומיין של
            דף המתנות לא ברשימה - הדף לא ייטען.
          </CardDescription>
        </div>
        <Button size="xs" variant="ghost" onClick={() => load(false)} disabled={!online || state === "loading"}>
          <RefreshCw className={state === "loading" ? "size-3 animate-spin" : "size-3"} />
          קרא מהמכשיר
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <Textarea
          rows={6}
          placeholder={state === "loading" ? "טוען מהמכשיר…" : "דומיין אחד בכל שורה"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={state === "loading" || (state !== "loaded" && online)}
          dir="ltr"
        />
        <LoadHint online={online} state={state} />
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={!online || state !== "loaded"}>
            <Save className="size-3.5" />
            עדכן רשימה
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function LoadHint({ online, state }: { online: boolean; state: LoadState }) {
  if (!online) {
    return <div className="text-xs text-muted-foreground">המכשיר לא מחובר - לא ניתן לקרוא או לעדכן.</div>
  }
  if (state === "loading") {
    return <div className="text-xs text-muted-foreground">טוען ערך נוכחי מהמכשיר…</div>
  }
  if (state === "error") {
    return <div className="text-xs text-amber-600">לא ניתן לטעון את הערך הנוכחי. השמירה נחסמה כדי למנוע דריסה בטעות - נסה שוב.</div>
  }
  return null
}

function RemoteConfigCard({ runCommand }: { runCommand: <T>(l: string, t: Parameters<typeof sendCommand>[1], p?: Record<string, unknown>) => Promise<T> }) {
  const confirm = useConfirm()
  const [url, setUrl] = React.useState("")
  const [protocol, setProtocol] = React.useState<"" | "sse" | "ws">("")
  const [enabled, setEnabled] = React.useState<"" | "true" | "false">("")

  async function apply() {
    const params: Record<string, unknown> = {}
    if (url.trim()) params.url = url.trim()
    if (protocol) params.protocol = protocol
    if (enabled) params.enabled = enabled === "true"
    if (Object.keys(params).length === 0) return toast.error("לא צוין שום שינוי")
    const ok = await confirm({
      title: "לעדכן את הגדרות השליטה מרחוק?",
      description:
        "המכשיר ינתק וייתחבר מחדש, אולי לכתובת אחרת. אם הכתובת שגויה הוא לא יחזור להתחבר וצריך יהיה לתקן זאת ידנית על הטאבלט.",
      confirmLabel: "עדכן",
      destructive: true,
    })
    if (!ok) return
    await runCommand("עדכון הגדרות שליטה מרחוק", "set_remote_config", params).catch(() => {})
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="size-4" />
          הגדרות שליטה מרחוק במכשיר
        </CardTitle>
        <CardDescription className="text-amber-600 dark:text-amber-400">
          משנה את כתובת השרת ואת סוג החיבור שהטאבלט מתחבר אליהם. כתובת שגויה כאן = טאבלט שלא יחזור
          להתחבר, ותיקון ידני עליו במקום. אל תיגע אלא אם אתה מעביר אותו לשרת אחר.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="כתובת שרת חדשה">
          <Input
            placeholder="https://wedding-gift-management.dinobycloud.com/api/connect-devices"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            dir="ltr"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="פרוטוקול">
            <select
              className="border-input bg-background h-9 rounded-md border px-3 text-sm"
              value={protocol}
              onChange={(e) => setProtocol(e.target.value as "" | "sse" | "ws")}
            >
              <option value="">ללא שינוי</option>
              <option value="sse">SSE</option>
              <option value="ws">WebSocket</option>
            </select>
          </Field>
          <Field label="מופעל">
            <select
              className="border-input bg-background h-9 rounded-md border px-3 text-sm"
              value={enabled}
              onChange={(e) => setEnabled(e.target.value as "" | "true" | "false")}
            >
              <option value="">ללא שינוי</option>
              <option value="true">כן</option>
              <option value="false">לא</option>
            </select>
          </Field>
        </div>
        <div className="rounded-md border border-amber-300/40 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          ⚠️ כתובת שגויה תנתק את המכשיר מהשרת הנוכחי לצמיתות עד תיקון ידני בטאבלט.
        </div>
        <div className="flex justify-end">
          <Button size="sm" variant="destructive" onClick={apply}>
            <Save className="size-3.5" />
            החל שינויים
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const SETTING_GROUPS: { key: SettingField["group"]; title: string; hint?: string }[] = [
  { key: "security", title: "סיסמאות והגנה", hint: "נכנס לתוקף רק לאחר הפעלה מחדש של האפליקציה" },
  { key: "keyboard", title: "מקלדת", hint: "נכנס לתוקף רק לאחר הפעלה מחדש" },
  {
    key: "refresh",
    title: "רענון אוטומטי",
    hint: "לפי שניות של היעדר נגיעה במסך, עם התראה שסופרת לאחור לפני הרענון",
  },
  { key: "ads", title: "פרסומות" },
  { key: "powerSaver", title: "חיסכון בחשמל" },
]

const SECRET_KEYS = new Set(["pass_settings", "pass_restart_app"])

function DeviceSettingsCard({
  deviceId, online, runCommand,
}: {
  deviceId: string
  online: boolean
  runCommand: <T = unknown>(
    label: string,
    type: Parameters<typeof sendCommand>[1],
    params?: Record<string, unknown>,
  ) => Promise<T>
}) {
  const confirm = useConfirm()
  const [settings, setSettings] = React.useState<DeviceSettings | null>(null)
  const [draft, setDraft] = React.useState<Record<string, SettingValue>>({})
  const [busy, setBusy] = React.useState(false)
  const [revealSecrets, setRevealSecrets] = React.useState(false)

  const load = React.useCallback(async () => {
    if (!online) return
    const data = await sendCommand<DeviceSettings>(deviceId, "get_settings").catch(() => null)
    if (data) {
      setSettings(data)
      setDraft({})
    }
  }, [deviceId, online])

  React.useEffect(() => {
    setSettings(null)
    setDraft({})
    load()
  }, [load])

  // רק מה שבאמת השתנה נשלח, כדי לא לדרוס ערכים שמישהו שינה על המכשיר בינתיים
  const dirtyKeys = React.useMemo(
    () => Object.keys(draft).filter((k) => draft[k] !== settings?.values[k]),
    [draft, settings],
  )
  const needsRestart = React.useMemo(
    () =>
      dirtyKeys.some((k) => settings?.schema.find((f) => f.key === k)?.live === false),
    [dirtyKeys, settings],
  )

  async function save(restart: boolean) {
    if (dirtyKeys.length === 0 || !settings) return
    const values: Record<string, SettingValue> = {}
    for (const k of dirtyKeys) values[k] = draft[k]

    if (restart) {
      const ok = await confirm({
        title: "לשמור ולהפעיל מחדש את האפליקציה?",
        description:
          "המכשיר יסגור את האפליקציה ויפתח אותה מחדש כדי להחיל את ההגדרות. " +
          "המסך יהבהב ואיסוף מתנות שנמצא באמצע ייקטע.",
        confirmLabel: "שמור והפעל מחדש",
        destructive: true,
      })
      if (!ok) return
    }

    setBusy(true)
    try {
      const res = await runCommand<SetSettingsResult>("שמירת הגדרות", "set_settings", {
        values,
        restart,
      })
      setSettings((prev) => (prev ? { ...prev, values: res.values } : prev))
      setDraft({})
      if (res.rejected.length > 0) {
        toast.warning(`נדחו ערכים לא תקינים: ${res.rejected.join(", ")}`)
      }
      if (res.restartRequired) {
        toast.message("חלק מההגדרות ייכנסו לתוקף רק בהפעלה הבאה של האפליקציה")
      }
    } catch {
      // runCommand כבר הודיע
    } finally {
      setBusy(false)
    }
  }

  async function restartApp() {
    const ok = await confirm({
      title: "להפעיל מחדש את האפליקציה?",
      description: "האפליקציה תיסגר ותיפתח מחדש. הטאבלט עצמו לא יאותחל.",
      confirmLabel: "הפעל מחדש",
      destructive: true,
    })
    if (!ok) return
    await runCommand("הפעלה מחדש", "restart_app").catch(() => null)
  }

  function fieldValue(f: SettingField): SettingValue {
    return draft[f.key] ?? settings?.values[f.key] ?? (f.type === "bool" ? false : "")
  }

  function setField(key: string, value: SettingValue) {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="size-4" />
            הגדרות האפליקציה
            {dirtyKeys.length > 0 ? (
              <Badge variant="outline">{dirtyKeys.length} שינויים לא שמורים</Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            ההגדרות של האפליקציה על הטאבלט. הרשימה נבנית ממה שהטאבלט מדווח, ורק שדות ששינית נשלחים
            אליו. חלק מההגדרות נכנסות לתוקף מיד, וחלק רק בהפעלה מחדש של האפליקציה.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button size="xs" variant="ghost" onClick={load} disabled={!online || busy}>
            <RotateCw className="size-3.5" />
            רענן
          </Button>
          <Button
            size="xs"
            variant="outline"
            onClick={restartApp}
            disabled={!online || busy}
            title="סוגר ופותח את האפליקציה על הטאבלט. הטאבלט עצמו לא מאותחל."
          >
            הפעל מחדש
          </Button>
          <Button
            size="xs"
            variant="outline"
            onClick={() => save(false)}
            disabled={!online || busy || dirtyKeys.length === 0}
          >
            <Save className="size-3.5" />
            שמור
          </Button>
          <Button
            size="xs"
            onClick={() => save(true)}
            disabled={!online || busy || dirtyKeys.length === 0}
          >
            שמור + הפעל מחדש
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!online ? (
          <div className="text-sm text-muted-foreground">המכשיר לא מחובר.</div>
        ) : !settings ? (
          <div className="text-sm text-muted-foreground">טוען הגדרות מהמכשיר…</div>
        ) : (
          <>
            {needsRestart ? (
              <div className="rounded-md border border-amber-500 p-2 text-xs">
                בין השינויים יש הגדרות שנקראות רק בעליית האפליקציה. שמירה רגילה תשמור אותן,
                אבל הן ייכנסו לתוקף רק בהפעלה הבאה - או עכשיו, עם "שמור + הפעל מחדש".
              </div>
            ) : null}

            {SETTING_GROUPS.map((group) => {
              const fields = settings.schema.filter((f) => f.group === group.key)
              if (fields.length === 0) return null
              return (
                <div key={group.key} className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-sm font-medium">{group.title}</h3>
                    {group.hint ? (
                      <span className="text-xs text-muted-foreground">{group.hint}</span>
                    ) : null}
                    {group.key === "security" ? (
                      <button
                        type="button"
                        className="text-xs underline text-muted-foreground hover:text-foreground"
                        onClick={() => setRevealSecrets((v) => !v)}
                      >
                        {revealSecrets ? "הסתר סיסמאות" : "הצג סיסמאות"}
                      </button>
                    ) : null}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {fields.map((f) => (
                      <SettingInput
                        key={f.key}
                        field={f}
                        value={fieldValue(f)}
                        dirty={dirtyKeys.includes(f.key)}
                        masked={SECRET_KEYS.has(f.key) && !revealSecrets}
                        disabled={busy}
                        onChange={(v) => setField(f.key, v)}
                      />
                    ))}
                  </div>
                  <Separator />
                </div>
              )
            })}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function SettingInput({
  field, value, dirty, masked, disabled, onChange,
}: {
  field: SettingField
  value: SettingValue
  dirty: boolean
  masked: boolean
  disabled: boolean
  onChange: (v: SettingValue) => void
}) {
  const label = (
    <Label className="flex items-center gap-1.5">
      {field.label}
      {dirty ? <span className="size-1.5 rounded-full bg-amber-500" /> : null}
    </Label>
  )

  if (field.type === "bool") {
    return (
      <label className="flex items-center gap-2 rounded-md border p-2">
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
    )
  }

  return (
    <div className="space-y-1.5">
      {label}
      {field.type === "enum" ? (
        <select
          className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
          value={String(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : field.type === "int" ? (
        <Input
          type="number"
          dir="ltr"
          min={field.min}
          max={field.max}
          value={String(value)}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      ) : (
        <Input
          type={masked ? "password" : "text"}
          dir="ltr"
          value={String(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  )
}

const DAY_NAMES = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"]

/** שורות הלוג של תהליך העדכון - כל השאר (WebView, טיימרים) הוא רעש כאן. */
const UPDATE_LOG_MARKERS = ["AppUpdateManager", "UpdateWorker", "UpdateScheduler"]

const EVENT_LABELS: Record<string, string> = {
  update_started: "הורדה החלה",
  update_installed: "ההתקנה בוצעה",
  update_none: "אין מה לעדכן",
  update_failed: "העדכון נכשל",
}

function UpdateMonitorDialog({
  title, logs, events, onClose,
}: {
  title: string
  logs: LogLine[]
  events: MonitorEvent[]
  onClose: () => void
}) {
  const [showAllLogs, setShowAllLogs] = React.useState(false)
  const scroller = React.useRef<HTMLDivElement>(null)

  const visible = React.useMemo(
    () =>
      showAllLogs
        ? logs
        : logs.filter((l) =>
            UPDATE_LOG_MARKERS.some((m) => (l.message || l.raw || "").includes(m)),
          ),
    [logs, showAllLogs],
  )

  React.useEffect(() => {
    const el = scroller.current
    if (el) el.scrollTop = el.scrollHeight
  }, [visible.length, events.length])

  const last = events[events.length - 1]
  const done = last && last.event !== "update_started"
  const failed = last?.event === "update_failed"

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="flex max-h-[85vh] w-full max-w-3xl flex-col">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Download className="size-4" />
            {title}
            {!last ? (
              <Badge variant="outline" className="gap-1">
                <RefreshCw className="size-3 animate-spin" />
                ממתין למכשיר…
              </Badge>
            ) : (
              <Badge variant={failed ? "destructive" : done ? "default" : "outline"} className="gap-1">
                {!done ? <RefreshCw className="size-3 animate-spin" /> : null}
                {EVENT_LABELS[last.event] ?? last.event}
              </Badge>
            )}
          </CardTitle>
          <Button size="xs" variant="ghost" onClick={onClose}>
            סגור
          </Button>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
          {events.length > 0 ? (
            <div className="space-y-1 text-sm">
              {events.map((e, i) => (
                <div key={`${e.event}-${i}`} className="flex flex-wrap items-center gap-2">
                  <span className={e.event === "update_failed" ? "text-rose-600 dark:text-rose-400" : ""}>
                    {EVENT_LABELS[e.event] ?? e.event}
                  </span>
                  {e.data.version ? (
                    <span className="text-muted-foreground" dir="ltr">
                      {e.data.packageName} {e.data.version}
                    </span>
                  ) : null}
                  {e.data.error ? (
                    <span className="font-mono text-xs text-rose-600 dark:text-rose-400" dir="ltr">
                      {e.data.error}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          <div
            ref={scroller}
            className="bg-muted/40 min-h-40 flex-1 overflow-auto rounded-md border p-3 font-mono text-xs leading-snug"
            dir="ltr"
          >
            {visible.length === 0 ? (
              <div className="text-muted-foreground">
                {showAllLogs ? "אין לוגים עדיין…" : "ממתין לשורות הלוג של תהליך העדכון…"}
              </div>
            ) : (
              visible.map((log, i) => (
                <div key={`mon-${i}`} className="whitespace-pre-wrap">
                  <span className="text-muted-foreground">{log.timestamp || ""}</span>{" "}
                  <span className={levelColor(log.level)}>[{log.level || "?"}]</span>{" "}
                  {log.message || log.raw || JSON.stringify(log)}
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showAllLogs}
                onChange={(e) => setShowAllLogs(e.target.checked)}
              />
              הצג את כל שורות הלוג ({logs.length})
            </label>
            {done && !failed ? (
              <span>
                המכשיר יעלה מחדש - הזרם החי ייעצר עד שיתחבר שוב.
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function UpdatesCard({
  deviceId, online, runCommand, onInstalled, beginMonitor,
}: {
  deviceId: string
  online: boolean
  runCommand: <T = unknown>(
    label: string,
    type: Parameters<typeof sendCommand>[1],
    params?: Record<string, unknown>,
  ) => Promise<T>
  onInstalled: () => void
  beginMonitor: (title: string) => Promise<void>
}) {
  const confirm = useConfirm()
  const [cfg, setCfg] = React.useState<UpdateConfig | null>(null)
  const [builds, setBuilds] = React.useState<AppBuild[]>([])
  const [selectedBuild, setSelectedBuild] = React.useState("")
  const [busy, setBusy] = React.useState(false)

  const load = React.useCallback(async () => {
    if (!online) return
    const data = await sendCommand<UpdateConfig>(deviceId, "get_update_config").catch(() => null)
    if (data) setCfg(data)
  }, [deviceId, online])

  React.useEffect(() => {
    setCfg(null)
    load()
    listBuilds().then(setBuilds).catch(() => setBuilds([]))
  }, [load])

  async function save(patch: Partial<UpdateConfig>) {
    setBusy(true)
    try {
      const next = await runCommand<UpdateConfig>("שמירת חלון עדכון", "set_update_config", patch)
      setCfg(next)
    } catch {
      // runCommand כבר הודיע על השגיאה
    } finally {
      setBusy(false)
    }
  }

  async function runUpdate() {
    const ok = await confirm({
      title: "להריץ עדכון עכשיו?",
      description:
        "המכשיר יבדוק מול השרת אם יש גרסה חדשה יותר, ואם כן - יוריד ויתקין אותה. " +
        "בסיום ההתקנה האפליקציה עולה מחדש, כך שאיסוף מתנות שנמצא באמצע ייקטע.",
      confirmLabel: "הרץ עדכון",
    })
    if (!ok) return
    // הזרם נדלק לפני הפקודה, אחרת השורות הראשונות של התהליך מתפספסות
    await beginMonitor("הרצת עדכון")
    await runCommand("הרצת עדכון", "run_update").catch(() => null)
  }

  async function install() {
    const build = builds.find((b) => b.id === selectedBuild)
    if (!build) return
    const ok = await confirm({
      title: `להתקין את ${build.version} על המכשיר הזה?`,
      description:
        "ההתקנה מיידית ומתעלמת מהשוואת הגרסאות - כולל התקנה חוזרת או ירידת גרסה. " +
        "האפליקציה תעלה מחדש בסיום.",
      confirmLabel: "התקן עכשיו",
      destructive: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      await beginMonitor(`התקנת ${build.version}`)
      await installBuild(deviceId, build.id)
      toast.success(`ההתקנה של ${build.version} נשלחה למכשיר`)
      onInstalled()
    } catch (err) {
      toast.error(`ההתקנה נכשלה: ${(err as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <Download className="size-4" />
            עדכוני אפליקציה
          </CardTitle>
          <CardDescription>
            מתי הטאבלט בודק בעצמו אם יצאה גרסה חדשה, ואיזו גרסה מותקנת עליו כרגע. הגרסאות עצמן
            מועלות ומסומנות בעמוד "גרסאות".
          </CardDescription>
        </div>
        <Button
          size="xs"
          variant="outline"
          onClick={runUpdate}
          disabled={!online || busy}
          title="בודק מיד מול השרת אם יש גרסה חדשה יותר, ואם כן מוריד ומתקין. האפליקציה תעלה מחדש."
        >
          הרץ עדכון עכשיו
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!online ? (
          <div className="text-sm text-muted-foreground">המכשיר לא מחובר.</div>
        ) : !cfg ? (
          <div className="text-sm text-muted-foreground">טוען הגדרות מהמכשיר…</div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label>עדכון אוטומטי</Label>
                <select
                  className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                  value={cfg.enabled ? "on" : "off"}
                  disabled={busy}
                  onChange={(e) => save({ enabled: e.target.value === "on" })}
                >
                  <option value="on">פעיל</option>
                  <option value="off">כבוי</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label>תדירות</Label>
                <select
                  className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                  value={cfg.frequency}
                  disabled={busy || !cfg.enabled}
                  onChange={(e) => save({ frequency: e.target.value as UpdateConfig["frequency"] })}
                >
                  <option value="weekly">שבועי</option>
                  <option value="daily">יומי</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label>יום</Label>
                <select
                  className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                  value={cfg.dayOfWeek}
                  disabled={busy || !cfg.enabled || cfg.frequency !== "weekly"}
                  onChange={(e) => save({ dayOfWeek: Number(e.target.value) })}
                >
                  {DAY_NAMES.map((name, i) => (
                    <option key={name} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label>שעה</Label>
                <Input
                  type="time"
                  dir="ltr"
                  disabled={busy || !cfg.enabled}
                  value={`${String(cfg.hour).padStart(2, "0")}:${String(cfg.minute).padStart(2, "0")}`}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(":").map(Number)
                    if (Number.isFinite(h) && Number.isFinite(m)) save({ hour: h, minute: m })
                  }}
                />
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              גרסה מותקנת: <span dir="ltr">{cfg.appVersion}</span>
              {cfg.enabled && cfg.nextRunAt
                ? ` · העדכון הבא: ${formatAbsolute(new Date(cfg.nextRunAt).toISOString())} (${cfg.timeZone})`
                : " · עדכון אוטומטי כבוי"}
            </div>

            <Separator />

            <div className="space-y-1.5">
              <Label>התקנת גרסה ספציפית עכשיו</Label>
              <div className="flex flex-wrap gap-2">
                <select
                  className="border-input bg-background h-9 min-w-64 flex-1 rounded-md border px-3 text-sm"
                  value={selectedBuild}
                  disabled={busy || builds.length === 0}
                  onChange={(e) => setSelectedBuild(e.target.value)}
                >
                  <option value="">- בחר גרסה -</option>
                  {builds.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.packageName} · {b.version}
                      {b.isCurrent ? " (נוכחית)" : ""}
                    </option>
                  ))}
                </select>
                <Button variant="outline" disabled={busy || !selectedBuild} onClick={install}>
                  התקן על המכשיר
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {builds.length === 0
                  ? `לא הועלו גרסאות. העלה APK בעמוד "גרסאות".`
                  : "עוקף את השוואת הגרסאות - מיועד לבדיקה על מכשיר בודד לפני הפצה."}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function LogsCard({
  tailLines, streaming, streamedLogs, onFetchTail, onClearStream, onToggleStream, disabled,
}: {
  tailLines: string[] | null
  streaming: boolean
  streamedLogs: LogLine[]
  onFetchTail: (lines?: number) => void
  onClearStream: () => void
  onToggleStream: () => void
  disabled: boolean
}) {
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <Terminal className="size-4" />
            לוגים
          </CardTitle>
          <CardDescription>
            הלוג של האפליקציה על הטאבלט - לאבחון תקלות. "שלוף" מביא צילום מצב חד-פעמי; "זרם חי"
            משדר בזמן אמת עד שתכבה אותו.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button
            size="xs"
            variant="outline"
            onClick={() => onFetchTail(200)}
            disabled={disabled}
            title="מושך את 200 השורות האחרונות מהלוג של הטאבלט."
          >
            שלוף 200 שורות
          </Button>
          <Button
            size="xs"
            variant={streaming ? "destructive" : "default"}
            onClick={onToggleStream}
            disabled={disabled}
            title="הטאבלט משדר את הלוג שלו בזמן אמת. כבה כשסיימת."
          >
            {streaming ? "כבה זרם חי" : "הפעל זרם חי"}
          </Button>
          <Button size="xs" variant="ghost" onClick={onClearStream} title="מנקה את התצוגה כאן בלבד.">
            נקה
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div
          className="bg-muted/40 max-h-80 overflow-auto rounded-md border p-3 font-mono text-xs leading-snug"
          dir="ltr"
        >
          {tailLines && tailLines.length > 0 && (
            <>
              {tailLines.map((line, i) => (
                <div key={`tail-${i}`} className="whitespace-pre-wrap">{line}</div>
              ))}
              <div className="my-2 border-t border-dashed" />
            </>
          )}
          {streamedLogs.length === 0 && !tailLines ? (
            <div className="text-muted-foreground">אין לוגים להציג.</div>
          ) : null}
          {streamedLogs.map((log, i) => (
            <div
              key={`stream-${i}`}
              className={log.backfill ? "whitespace-pre-wrap opacity-60" : "whitespace-pre-wrap"}
              title={log.backfill ? "שורה משוחזרת מקובץ הלוג של המכשיר" : undefined}
            >
              <span className="text-muted-foreground">{log.timestamp || ""}</span>{" "}
              <span className={levelColor(log.level)}>[{log.level || "?"}]</span>{" "}
              {log.message || log.raw || JSON.stringify(log)}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function levelColor(level?: string) {
  switch (level) {
    case "ERROR": return "text-rose-600 dark:text-rose-400"
    case "WARNING": return "text-amber-600 dark:text-amber-400"
    case "DEBUG": return "text-muted-foreground"
    default: return "text-emerald-700 dark:text-emerald-300"
  }
}

function hasDetails(e: DeviceEvent) {
  return Boolean(e.correlationId) || (e.payload !== null && e.payload !== undefined)
}

function eventMatches(e: DeviceEvent, query: string) {
  const haystack = [
    e.kind,
    e.name,
    e.error,
    e.correlationId,
    e.payload === null || e.payload === undefined ? "" : JSON.stringify(e.payload),
  ].join(" ").toLowerCase()
  return haystack.includes(query)
}

function EventsCard({
  events,
  hasMore,
  loadingMore,
  onRefresh,
  onLoadMore,
}: {
  events: DeviceEvent[] | null
  hasMore: boolean
  loadingMore: boolean
  onRefresh: () => void
  onLoadMore: () => void
}) {
  const [expanded, setExpanded] = React.useState<Set<number>>(() => new Set())
  const [query, setQuery] = React.useState("")

  function toggle(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const trimmed = query.trim().toLowerCase()
  const visible = React.useMemo(
    () => (!events ? [] : trimmed ? events.filter((e) => eventMatches(e, trimmed)) : events),
    [events, trimmed],
  )

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="space-y-1.5">
          <CardTitle>היסטוריית אירועים</CardTitle>
          <CardDescription>
            כל מה שקרה למכשיר לפי השרת: חיבורים, ניתוקים, פקודות והתשובות עליהן. לחיצה על שורה
            פותחת את התוכן המלא שלה.
          </CardDescription>
        </div>
        <Button size="xs" variant="ghost" onClick={onRefresh}>
          <RefreshCw className="size-3" />
          רענן
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 start-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-8 ps-8 text-sm"
            placeholder="חיפוש לפי סוג, שם, שגיאה או תוכן…"
            value={query}
            onChange={(ev) => setQuery(ev.target.value)}
          />
        </div>
        {!events ? (
          <div className="text-sm text-muted-foreground">טוען…</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-muted-foreground">אין אירועים.</div>
        ) : visible.length === 0 ? (
          <div className="text-sm text-muted-foreground">אין אירועים תואמים מבין {events.length} שנטענו.</div>
        ) : (
          <div className="max-h-96 overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground">
                <tr className="border-b">
                  <th className="w-6 px-2 py-1.5" />
                  <th className="px-2 py-1.5 text-start">זמן</th>
                  <th className="px-2 py-1.5 text-start">סוג</th>
                  <th className="px-2 py-1.5 text-start">שם</th>
                  <th className="px-2 py-1.5 text-start">סטטוס</th>
                  <th className="px-2 py-1.5 text-start">שגיאה</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => {
                  const details = hasDetails(e)
                  const isOpen = expanded.has(e.id)
                  return (
                    <React.Fragment key={e.id}>
                      <tr
                        className={`border-b last:border-b-0 ${details ? "cursor-pointer hover:bg-muted/50" : ""}`}
                        onClick={details ? () => toggle(e.id) : undefined}
                      >
                        <td className="px-2 py-1.5 text-muted-foreground">
                          {details ? (
                            isOpen ? <ChevronDown className="size-3.5" /> : <ChevronLeft className="size-3.5" />
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-xs">{formatAbsolute(e.createdAt)}</td>
                        <td className="px-2 py-1.5"><Badge variant="outline">{e.kind}</Badge></td>
                        <td className="px-2 py-1.5 font-mono text-xs">{e.name || "-"}</td>
                        <td className="px-2 py-1.5">
                          {e.ok === true ? <Badge variant="success">OK</Badge> :
                           e.ok === false ? <Badge variant="destructive">FAIL</Badge> : "-"}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-rose-600">{e.error || ""}</td>
                      </tr>
                      {details && isOpen ? (
                        <tr className="border-b last:border-b-0 bg-muted/30">
                          <td />
                          <td colSpan={5} className="px-2 py-2">
                            {e.correlationId ? (
                              <div className="mb-1.5 text-xs text-muted-foreground">
                                מזהה קורלציה: <span className="font-mono">{e.correlationId}</span>
                              </div>
                            ) : null}
                            {e.payload !== null && e.payload !== undefined ? (
                              <pre dir="ltr" className="max-h-64 overflow-auto rounded-md bg-background p-2 text-start font-mono text-xs">
                                {JSON.stringify(e.payload, null, 2)}
                              </pre>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {events && events.length > 0 ? (
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {trimmed
                ? `${visible.length} מתוך ${events.length} שנטענו`
                : `${events.length} אירועים נטענו`}
            </span>
            {hasMore ? (
              <Button size="xs" variant="outline" onClick={onLoadMore} disabled={loadingMore}>
                {loadingMore ? "טוען…" : `טען עוד ${EVENTS_PAGE_SIZE}`}
              </Button>
            ) : (
              <span>הגעת לתחילת ההיסטוריה</span>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function DangerZoneCard({ device }: { device: Device }) {
  const confirm = useConfirm()

  async function remove() {
    const ok = await confirm({
      title: `למחוק את ${device.displayLabel}?`,
      description: (
        <>
          המכשיר, יומן האירועים שלו וכל היסטוריית הסטטוס יימחקו לצמיתות.{" "}
          <strong>פעולה זו אינה ניתנת לשחזור.</strong>
        </>
      ),
      confirmLabel: "מחק לצמיתות",
      destructive: true,
    })
    if (!ok) return
    try {
      await api(`/api/devices/${encodeURIComponent(device.deviceId)}`, { method: "DELETE" })
      toast.success("נמחק")
      window.location.href = "/"
    } catch (err) {
      toast.error(`מחיקה נכשלה: ${(err as Error).message}`)
    }
  }
  return (
    <Card className="border-destructive/30">
      <CardHeader>
        <CardTitle className="text-destructive">אזור מסוכן</CardTitle>
        <CardDescription>
          מחיקה מסירה מהשרת את המכשיר, יומן האירועים והיסטוריית הסטטוס שלו. אפשרית רק כשהוא לא
          מחובר. אם יתחבר שוב - יופיע כמכשיר חדש, בלי היסטוריה ובלי התווית שנתת לו.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button size="sm" variant="destructive" onClick={remove} disabled={device.online}>
          <Trash2 className="size-3.5" />
          מחק מכשיר והיסטוריה
        </Button>
        {device.online ? (
          <div className="mt-2 text-xs text-muted-foreground">לא ניתן למחוק כשהמכשיר מחובר.</div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
    </div>
  )
}

function Info({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="inline-flex items-center gap-1 truncate">{icon}{value}</span>
    </div>
  )
}

function BatteryIcon({ percent, charging }: { percent?: number; charging?: boolean }) {
  if (charging) return <BatteryCharging className="size-4 text-emerald-600" />
  if (percent == null) return <BatteryMedium className="size-4 text-muted-foreground" />
  if (percent < 20) return <BatteryLow className="size-4 text-rose-600" />
  return <BatteryMedium className="size-4 text-muted-foreground" />
}
